import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import {
  ServerPrivateRegionNodeDetails,
  ServerPrivateRegionNodeHardware,
  ServerPrivateRegionNodeSpec,
  ServerPrivateRegionVGpuSpec,
} from '@/api/type-server'
import EditIcon from '@/assets/icons/edit.svg?react'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?react'
import PauseIcon from '@/assets/icons/pause.svg?react'
import ResumeIcon from '@/assets/icons/resume.svg?react'
import { Loading, Trans } from '@/components'
import { message } from '@/components/base/Message'
import { cn } from '@/utils/style'

import { SpecCategory } from '../constants/specCategory'
import { getNodeStatusColor, getNodeStatusTextKey, NodeStatus } from '../utils/nodeFormatters'
import { EditInstanceSpecModal } from './EditInstanceSpecModal'
import { GpusInfoTable } from './GpusInfoTable'
import { InstanceSpecPreviewModal } from './InstanceSpecPreviewModal'
import { InstanceSpecSection } from './InstanceSpecSection'
import { StandbyConfirmModal } from './StandbyConfirmModal'
import { StandbyRequiredModal } from './StandbyRequiredModal'

interface NodeDetailContentProps {
  node: ServerPrivateRegionNodeDetails
  onRefetch?: () => void
}

type TabType = 'server-info' | 'gpus-info' | 'instance-spec'

const HARDWARE_PAGE_SIZE = 10

// Tab button styles
const TAB_BASE_STYLE = 'px-4 py-2 rounded-lg t-x-sb transition-colors'
const TAB_ACTIVE_STYLE = 'bg-highlight/10 text-highlight'
const TAB_INACTIVE_STYLE = 'bg-neutral-100 text-primary'

export function NodeDetailContent({ node, onRefetch }: NodeDetailContentProps) {
  const { t } = useTranslation('privateRegion')
  const [activeTab, setActiveTab] = useState<TabType>('server-info')
  const [hardwares, setHardwares] = useState<ServerPrivateRegionNodeHardware[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hardwarePage, setHardwarePage] = useState(1)
  const [hardwareTotal, setHardwareTotal] = useState(0)
  const [standbyModalOpen, setStandbyModalOpen] = useState(false)

  // Instance Spec state - null means not yet loaded
  const [specs, setSpecs] = useState<ServerPrivateRegionNodeSpec[] | null>(null)
  const [vGpuSpecs, setVGpuSpecs] = useState<ServerPrivateRegionVGpuSpec[]>([])
  const [specsLoading, setSpecsLoading] = useState(false)
  const [specsError, setSpecsError] = useState<string | null>(null)

  // Edit Instance Spec Modal state
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [editedSpecs, setEditedSpecs] = useState<ServerPrivateRegionNodeSpec[]>([])
  const [standbyRequiredModalOpen, setStandbyRequiredModalOpen] = useState(false)

  const fetchHardwares = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await API.getPrivateRegionNodeHardwares({
        res_id: node.resID,
        page: hardwarePage,
        per_page: HARDWARE_PAGE_SIZE,
      })
      setHardwares(response.hardwares || [])
      setHardwareTotal(response.pagination.total)
    } catch (err) {
      setError(t('errors.failedToLoadGpuInfo'))
    } finally {
      setLoading(false)
    }
  }, [node.resID, hardwarePage, t])

  // Re-fetch hardware data when page changes (skip initial load, handled by tab switch)
  useEffect(() => {
    if (hardwares !== null) {
      fetchHardwares()
    }
  }, [hardwarePage])

  const fetchSpecs = useCallback(async () => {
    setSpecsLoading(true)
    setSpecsError(null)
    try {
      const [nodeSpecsRes, vGpuSpecsRes] = await Promise.all([
        API.getPrivateRegionNodeSpecs({ res_id: node.resID }),
        API.getPrivateRegionVGpuSpecs({ res_id: node.resID }),
      ])
      setSpecs(nodeSpecsRes.specs || [])
      setVGpuSpecs(vGpuSpecsRes.specs || [])
    } catch (err) {
      setSpecsError(t('errors.failedToLoadInstanceSpec'))
    } finally {
      setSpecsLoading(false)
    }
  }, [node.resID, t])

  const handleTabChange = useCallback(
    async (tab: TabType) => {
      setActiveTab(tab)

      // Lazy load hardware data when switching to GPUs Info tab
      if (tab === 'gpus-info' && hardwares === null && !loading) {
        fetchHardwares()
      }

      // Lazy load specs data when switching to Instance Spec tab
      if (tab === 'instance-spec' && specs === null && !specsLoading) {
        fetchSpecs()
      }
    },
    [hardwares, loading, fetchHardwares, specs, specsLoading, fetchSpecs],
  )

  const handleRetryFetchHardwares = useCallback(() => {
    fetchHardwares()
  }, [fetchHardwares])

  const handleOpenStandbyModal = useCallback(() => {
    setStandbyModalOpen(true)
  }, [])

  const handleCloseStandbyModal = useCallback(() => {
    setStandbyModalOpen(false)
  }, [])

  const handleSwitchToStandby = useCallback(async () => {
    const targetStatus = node.status === NodeStatus.Online ? NodeStatus.Standby : NodeStatus.Online
    try {
      const res = await API.updatePrivateRegionNodeStatus({
        res_id: node.resID,
        status: targetStatus,
      })
      if (res.code !== 0) {
        message.error(t('errors.failedToSwitchStatus'))
        return
      }
      setStandbyModalOpen(false)
      setHardwares(null)
      setHardwarePage(1)
      message.success(
        t(
          targetStatus === NodeStatus.Standby
            ? 'nodeDetail.switchToStandbySuccess'
            : 'nodeDetail.switchToOnlineSuccess',
        ),
      )
      onRefetch?.()
    } catch (err) {
      // Error handled by API layer
    }
  }, [node.resID, node.status, onRefetch])

  // Edit Instance Spec handlers
  const handleOpenEditModal = useCallback(() => {
    if (node.status === NodeStatus.Online) {
      setStandbyRequiredModalOpen(true)
      return
    }
    setEditModalOpen(true)
  }, [node.status])

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false)
    setEditedSpecs([])
  }, [])

  const handleVerifySuccess = useCallback((verifiedSpecs: ServerPrivateRegionNodeSpec[]) => {
    setEditedSpecs(verifiedSpecs)
    setEditModalOpen(false)
    setPreviewModalOpen(true)
  }, [])

  const handleClosePreviewModal = useCallback(() => {
    setPreviewModalOpen(false)
  }, [])

  const handlePreviousFromPreview = useCallback(() => {
    setPreviewModalOpen(false)
    setEditModalOpen(true)
  }, [])

  const handleSaveSuccess = useCallback(() => {
    setPreviewModalOpen(false)
    setEditedSpecs([])
    fetchSpecs()
  }, [fetchSpecs])

  const handleCloseStandbyRequiredModal = useCallback(() => {
    setStandbyRequiredModalOpen(false)
  }, [])

  const handleStandbyForEditSuccess = useCallback(() => {
    setStandbyRequiredModalOpen(false)
    setHardwares(null)
    onRefetch?.()
    setEditModalOpen(true)
  }, [onRefetch])

  const getTabClassName = useCallback(
    (tab: TabType) => cn(TAB_BASE_STYLE, activeTab === tab ? TAB_ACTIVE_STYLE : TAB_INACTIVE_STYLE),
    [activeTab],
  )

  // Filter specs by category
  const specsData = specs || []
  const vgpuSpecs = specsData.filter((spec) => spec.category === SpecCategory.VGPU)
  const gpuSpecs = specsData.filter((spec) => spec.category === SpecCategory.GPU)
  const cpuSpecs = specsData.filter((spec) => spec.category === SpecCategory.CPU)

  return (
    <div className="flex flex-col gap-2 w-full px-4 pb-4">
      {/* Tabs */}
      <div className="flex gap-2">
        <button
          className={getTabClassName('server-info')}
          onClick={() => handleTabChange('server-info')}
        >
          <Trans ns="privateRegion" i18nKey="nodeDetail.serverInfo">
            Server Info
          </Trans>
        </button>
        <button
          className={getTabClassName('gpus-info')}
          onClick={() => handleTabChange('gpus-info')}
        >
          <Trans ns="privateRegion" i18nKey="nodeDetail.gpusInfo">
            GPUs Info
          </Trans>
        </button>
        <button
          className={getTabClassName('instance-spec')}
          onClick={() => handleTabChange('instance-spec')}
        >
          <Trans ns="privateRegion" i18nKey="nodeDetail.instanceSpec">
            Instance Spec
          </Trans>
        </button>
      </div>

      {/* Content */}
      <div className="bg-neutral-50 rounded-lg p-4">
        {activeTab === 'server-info' && (
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex-1 min-w-[226px]">
                <div className="t-xs text-secondary mb-1">
                  <Trans ns="privateRegion" i18nKey="nodeDetail.serverName">
                    Server Name
                  </Trans>
                </div>
                <div className="t-sb text-primary">{node.name}</div>
              </div>
              <div className="flex-1 min-w-[226px]">
                <div className="t-xs text-secondary mb-1">
                  <Trans ns="privateRegion" i18nKey="nodeDetail.serverRegion">
                    Server Region
                  </Trans>
                </div>
                <div className="t-sb text-primary">{node.regionInfo.name}</div>
              </div>
              <div className="flex-1 min-w-[226px]">
                <div className="t-xs text-secondary mb-1">
                  <Trans ns="privateRegion" i18nKey="nodeDetail.serverStatus">
                    Server Status
                  </Trans>
                </div>
                <div className={cn('t-sb', getNodeStatusColor(node.status))}>
                  {t(getNodeStatusTextKey(node.status))}
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1 min-w-[226px]">
                <div className="t-xs text-secondary mb-1">
                  <Trans ns="privateRegion" i18nKey="computeNode.gpuType">
                    GPU Type
                  </Trans>
                </div>
                <div className="t-sb text-primary">{node.gpuModel || '-'}</div>
              </div>
              <div className="flex-1 min-w-[226px]">
                <div className="t-xs text-secondary mb-1">
                  <Trans ns="privateRegion" i18nKey="computeNode.cpuModel">
                    CPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">{node.cpuModel || '-'}</div>
              </div>
              <div className="flex-1 min-w-[226px] opacity-0" aria-hidden="true">
                <div className="t-xs text-secondary mb-1">-</div>
                <div className="t-sb text-primary">-</div>
              </div>
            </div>

            {/* Node Monitor Link */}
            <div className="border-t border-neutral-200 pt-3">
              <div className="flex space-x-4">
                {node.status === NodeStatus.Online && (
                  <button
                    className="inline-flex items-center gap-1 text-highlight t-x-sb"
                    onClick={handleOpenStandbyModal}
                  >
                    <PauseIcon className="w-4 h-4" />
                    <Trans ns="privateRegion" i18nKey="nodeDetail.switchToStandby">
                      Switch to Standby
                    </Trans>
                  </button>
                )}
                {node.status === NodeStatus.Standby && (
                  <button
                    className="inline-flex items-center gap-1 text-highlight t-x-sb"
                    onClick={handleOpenStandbyModal}
                  >
                    <ResumeIcon className="w-4 h-4" />
                    <Trans ns="privateRegion" i18nKey="nodeDetail.switchToOnline">
                      Switch to Online
                    </Trans>
                  </button>
                )}

                <Link
                  to="/private-region/monitor"
                  state={{ node }}
                  className="inline-flex items-center gap-1 text-highlight t-x-sb"
                >
                  <Trans ns="privateRegion" i18nKey="nodeDetail.nodeMonitor">
                    Node Monitor
                  </Trans>
                  <ExternalLinkIcon className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gpus-info' && (
          <GpusInfoTable
            hardwares={hardwares}
            loading={loading}
            error={error}
            onRetry={handleRetryFetchHardwares}
            total={hardwareTotal}
            page={hardwarePage}
            pageSize={HARDWARE_PAGE_SIZE}
            onPageChange={setHardwarePage}
          />
        )}

        {activeTab === 'instance-spec' && (
          <div className="flex flex-col gap-4">
            {specsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loading />
              </div>
            ) : specsError ? (
              <div className="text-red-500 t-s text-center py-8">
                {specsError}
                <button className="ml-2 text-highlight underline" onClick={fetchSpecs}>
                  {t('common.retry')}
                </button>
              </div>
            ) : specs === null ? null : specsData.length === 0 ? (
              <div className="text-tertiary t-s text-center py-8">
                <Trans ns="privateRegion" i18nKey="common.noData">
                  No data
                </Trans>
              </div>
            ) : (
              <>
                {vgpuSpecs.map((spec) => (
                  <InstanceSpecSection
                    key={spec.resID}
                    spec={spec}
                    category={SpecCategory.VGPU}
                    nodeTotalCpuCores={node.cpuCores}
                    nodeTotalRamMB={node.ramMB}
                    nodeTotalDiskGB={node.diskGB}
                  />
                ))}
                {gpuSpecs.map((spec) => (
                  <InstanceSpecSection
                    key={spec.resID}
                    spec={spec}
                    category={SpecCategory.GPU}
                    nodeTotalCpuCores={node.cpuCores}
                    nodeTotalRamMB={node.ramMB}
                    nodeTotalDiskGB={node.diskGB}
                  />
                ))}
                {cpuSpecs.map((spec) => (
                  <InstanceSpecSection
                    key={spec.resID}
                    spec={spec}
                    category={SpecCategory.CPU}
                    nodeTotalCpuCores={node.cpuCores}
                    nodeTotalRamMB={node.ramMB}
                    nodeTotalDiskGB={node.diskGB}
                  />
                ))}

                {/* Edit Link */}
                <div className="border-t border-neutral-200 pt-3">
                  <button
                    className={cn(
                      'flex items-center gap-1 t-x-sb',
                      node.status === NodeStatus.Offline
                        ? 'text-tertiary pointer-events-none'
                        : 'text-highlight',
                    )}
                    onClick={handleOpenEditModal}
                    disabled={node.status === NodeStatus.Offline}
                  >
                    <EditIcon className="w-[14px] h-[14px]" />
                    <Trans ns="privateRegion" i18nKey="instanceSpec.edit">
                      Edit
                    </Trans>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Switch to Standby Modal */}
      <StandbyConfirmModal
        open={standbyModalOpen}
        mode={node.status === NodeStatus.Online ? 'standby' : 'online'}
        resID={node.resID}
        onClose={handleCloseStandbyModal}
        onConfirm={handleSwitchToStandby}
      />

      {/* Standby Required Modal (for editing when Online) */}
      <StandbyRequiredModal
        open={standbyRequiredModalOpen}
        resID={node.resID}
        onClose={handleCloseStandbyRequiredModal}
        onSwitchSuccess={handleStandbyForEditSuccess}
      />

      {/* Edit Instance Spec Modal */}
      <EditInstanceSpecModal
        open={editModalOpen}
        node={node}
        specs={editedSpecs.length > 0 ? editedSpecs : specs || []}
        vgpuSpecs={vGpuSpecs}
        onClose={handleCloseEditModal}
        onVerifySuccess={handleVerifySuccess}
      />

      {/* Instance Spec Preview Modal */}
      <InstanceSpecPreviewModal
        open={previewModalOpen}
        node={node}
        specs={editedSpecs}
        vgpuSpecs={vGpuSpecs}
        onClose={handleClosePreviewModal}
        onPrevious={handlePreviousFromPreview}
        onSaveSuccess={handleSaveSuccess}
      />
    </div>
  )
}
